import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table,
  Spinner,
  Badge,
  Pagination,
  FormSelect
} from 'react-bootstrap';
import { FaEdit, FaSearch, FaPlus, FaTrash } from 'react-icons/fa';
import UserService from '../services/UserService';
import 'react-toastify/dist/ReactToastify.css';
import ConfirmModal from './ConfirmModal';
import { toast } from 'react-toastify';
import UserAdd from './UserAdd';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleSaveUser = (savedUser) => {
    fetchUsers();
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowAddModal(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowConfirmModal(true);
  }

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowAddModal(true);
  };

  const perPageOptions = [5, 10, 20, 50, 100];

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await UserService.getUserList(
        searchTerm,
        selectedRole,
        currentPage,
        perPage
      );
      
      setUsers(response.data.data);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Foydalanuvchilarni yuklashda xatolik yuz berdi');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await UserService.getUserRoles();
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [currentPage, perPage, searchTerm, selectedRole, sortField, sortOrder]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleRoleChange = (e) => {
    setSelectedRole(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    try {
      // Call your delete API here
      // await UserService.deleteUser(userToDelete.id);
      let response = UserService.deleteUser(userToDelete.id);
      if (response.success === true) {
        toast.success('Foydalanuvchi muvaffaqiyatli o\'chirildi');
      }else {
        toast.error('Foydalanuvchini o\'chirishda xatolik yuz berdi');
      }
      fetchUsers();
    } catch (error) {
      toast.error('Foydalanuvchini o\'chirishda xatolik yuz berdi');
      console.error('Error deleting user:', error);
    } finally {
      setShowConfirmModal(false);
      setUserToDelete(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getRoleBadges = (userRoles) => {
    if (!userRoles || userRoles.length === 0) return 'N/A';
    return userRoles.map((role, index) => (
      <Badge key={index} bg="primary" className="me-1">
        {role.name}
      </Badge>
    ));
  };

  const renderPagination = () => {
    const items = [];
    const totalPages = Math.ceil(totalItems / perPage);
    const maxPageButtons = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      />
    );

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage >= totalPages}
      />
    );

    return items;
  };

  return (
    <Container fluid className="py-4">
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Foydalanuvchilar</h5>
          <Button variant="primary" size="sm" onClick={handleAddUser}>
            <FaPlus className="me-2" /> Yangi qo'shish
          </Button>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-4">
            <Row>
              <Col md={4}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Qidirish..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button variant="outline-secondary" type="submit">
                    <FaSearch />
                  </Button>
                </InputGroup>
              </Col>
              <Col md={3}>
                <FormSelect
                  value={selectedRole}
                  onChange={handleRoleChange}
                >
                  <option value="">Barcha rollar</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </FormSelect>
              </Col>
            </Row>
          </Form>

          <div className="table-responsive">
            <Table striped bordered hover className="mb-0">
              <thead>
                <tr>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('id')}
                  >
                    ID {getSortIcon('id')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    Ism {getSortIcon('name')}
                  </th>
                  <th>Telefon</th>
                  <th>Email</th>
                  <th>Prava raqami</th>
                  <th>Rollar</th>
                  <th>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.name || 'N/A'}</td>
                      <td>{user.phone || 'N/A'}</td>
                      <td>{user.email || 'N/A'}</td>
                      <td>{user.prava_raqam || 'N/A'}</td>
                      <td>{getRoleBadges(user.roles)}</td>
                      <td>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-1"
                          title="Tahrirlash"
                          onClick={() => handleEditUser(user)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          title="O'chirish"
                          onClick={() => {
                            handleDeleteUser(user);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      Ma'lumot topilmadi
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="d-flex align-items-center">
              <span className="me-2">Sahifada:</span>
              <FormSelect
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
                style={{ width: 'auto' }}
                size="sm"
              >
                {perPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option} ta
                  </option>
                ))}
              </FormSelect>
              {!loading && users.length > 0 && (
                <span className="ms-2">
                  Jami {totalItems} ta
                </span>
              )}
            </div>
            {!loading && users.length > 0 && (
              <Pagination className="mb-0">
                {renderPagination()}
              </Pagination>
            )}
          </div>
        </Card.Body>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Foydalanuvchini o'chirish"
        message={`Rostan ham "${userToDelete?.name || 'Ushbu foydalanuvchi'}" ni o'chirmoqchimisiz?`}
      />

      <UserAdd
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        user={selectedUser}
        onSave={handleSaveUser}
      />
    </Container>
  );
};

export default Users;
